/**
 * HTTP(S) URL guard — el ÚNICO predicado que usan las tres capas que tocan una
 * URL almacenada antes de que acabe en un `href`.
 *
 * ⚠️ POR QUÉ EXISTE. Medido el 2026-08-23 con la zod real de este repo (4.1.5)
 * sobre `z.string().url({ message: 'Block explorer must be a valid URL' })`:
 *
 *     javascript:alert(document.cookie)          ACEPTADO
 *     data:text/html,<script>alert(1)</script>   ACEPTADO
 *     htp:/bad                                   ACEPTADO
 *     ftp://x.com                                ACEPTADO
 *     https://etherscan.io                       ACEPTADO   ← el único esperado
 *
 * Zod 4 no restringe el esquema en `.url()`, y `new URL(value)` a secas es aún
 * más débil: es justamente en lo que `.url()` se apoya. Es decir, «¿esto es una
 * URL?» NO es la misma pregunta que «¿esto se puede poner en un href?», y un
 * validador cuyo mensaje promete una URL tiene que responder la segunda.
 *
 * 🔑 COMPARTIDO, NO ESPEJADO. El formulario de admin, los validadores de la API
 * y el constructor de enlaces tienen que coincidir byte a byte: si cada capa
 * lleva su propia copia, la primera que se retoque abre el hueco de nuevo. Un
 * segundo validador que se desincronice sería un bug nuevo, no una defensa.
 */

/**
 * True cuando `value` parsea como URL absoluta Y lleva esquema http o https.
 *
 * Ojo: `new URL('htp:/bad')` NO lanza — WHATWG admite cualquier esquema con
 * ruta opaca — así que lo que rechaza ese valor es la comprobación de
 * `protocol`, no el try/catch.
 */
export function isHttpOrHttpsUrl(value: string): boolean {
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return false
  }
  return parsed.protocol === 'http:' || parsed.protocol === 'https:'
}
